import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import styles from './SearchableSelect.module.css';

export interface SelectOption {
  value: string | number;
  label: string;
  subLabel?: string;
  searchTerms?: string; // Additional searchable text (e.g., codes, phone, email)
}

export interface SearchableSelectProps {
  options: SelectOption[];
  value?: string | number | null;
  onChange: (value: string | number | null) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  loading?: boolean;
  clearable?: boolean;
  fullWidth?: boolean;
  onSearch?: (query: string) => void;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Seleziona...',
  label,
  error,
  disabled = false,
  loading = false,
  clearable = true,
  fullWidth = true,
  onSearch,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedOption = useMemo(() => {
    return options.find(opt => opt.value === value);
  }, [options, value]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(opt =>
      opt.label.toLowerCase().includes(query) ||
      (opt.subLabel && opt.subLabel.toLowerCase().includes(query)) ||
      (opt.searchTerms && opt.searchTerms.toLowerCase().includes(query))
    );
  }, [options, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (onSearch) {
      const timer = setTimeout(() => {
        onSearch(searchQuery);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchQuery, onSearch]);

  const handleSelect = (opt: SelectOption) => {
    onChange(opt.value);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchQuery('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    } else if (e.key === 'Enter' && filteredOptions.length > 0) {
      handleSelect(filteredOptions[0]);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.wrapper} ${fullWidth ? styles.fullWidth : ''}`}
    >
      {label && <label className={styles.label}>{label}</label>}

      <div
        className={`${styles.control} ${isOpen ? styles.focused : ''} ${error ? styles.error : ''} ${disabled ? styles.disabled : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <div className={styles.inputWrapper} onClick={(e) => e.stopPropagation()}>
            <Search size={16} className={styles.searchIcon} />
            <input
              ref={inputRef}
              type="text"
              className={styles.input}
              value={searchQuery}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={selectedOption ? selectedOption.label : placeholder}
              disabled={disabled}
            />
          </div>
        ) : (
          <div className={styles.valueDisplay}>
            {selectedOption ? (
              <div className={styles.selectedValue}>
                <span className={styles.valueLabel}>{selectedOption.label}</span>
                {selectedOption.subLabel && (
                  <span className={styles.valueSubLabel}>{selectedOption.subLabel}</span>
                )}
              </div>
            ) : (
              <span className={styles.placeholder}>{placeholder}</span>
            )}
          </div>
        )}

        <div className={styles.actions}>
          {clearable && selectedOption && !disabled && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              tabIndex={-1}
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown
            size={18}
            className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}
          />
        </div>
      </div>

      {isOpen && (
        <div className={styles.dropdown}>
          {loading ? (
            <div className={styles.loadingState}>Caricamento...</div>
          ) : filteredOptions.length === 0 ? (
            <div className={styles.emptyState}>
              {searchQuery ? 'Nessun risultato' : 'Nessuna opzione disponibile'}
            </div>
          ) : (
            <ul className={styles.optionsList}>
              {filteredOptions.map((opt) => (
                <li
                  key={opt.value}
                  className={`${styles.option} ${opt.value === value ? styles.optionSelected : ''}`}
                  onClick={() => handleSelect(opt)}
                >
                  <span className={styles.optionLabel}>{opt.label}</span>
                  {opt.subLabel && (
                    <span className={styles.optionSubLabel}>{opt.subLabel}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  );
};

export default SearchableSelect;
